import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/ChatPage.css";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import cpyIcon from "../assets/UserIcons/Copy.svg";
import speakerIcon from "../assets/UserIcons/Speaker.svg";
import { branding } from "../utils/branding";

const ChatSharedPage = () => {
  const { session_id } = useParams();
  const [messages, setMessages] = useState([]);
  const chatEndRef = useRef(null);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchSharedHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/chat/shared/${session_id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch shared session");
        }

        const data = await response.json();

        const formatted = data.history.map((msg) => ({
          id: msg._id,
          sender: msg.sender,
          message: msg.message,
          time: msg.time,
          userName: msg.userName,
          userPic: msg.userPic,
        }));

        setMessages(formatted);
      } catch (err) {
        console.error("Error loading shared chat:", err);
      }
    };

    if (session_id) fetchSharedHistory();
  }, [session_id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showSuccessToast("Text copied to clipboard!");
      } else {
        // ✅ Fallback for older browsers / Safari / HTTP
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        showSuccessToast("Text copied to clipboard!");
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  let currentUtterance = null;

  const handleTextToSpeech = (text) => {
    // If already speaking -> stop it first
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      // If same message clicked again → stop sound
      if (currentUtterance && currentUtterance.text === text) {
        currentUtterance = null;
        return;
      }
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    currentUtterance = utterance;

    // Optional: Set voice, pitch, rate, etc.
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    // When finished → reset current utterance
    utterance.onend = () => {
      currentUtterance = null;
    };

    // Speak
    window.speechSynthesis.speak(utterance);
  };

  const renderMessage = (msg, index) => {
    const isBot = msg.sender === "bot";
    const profile = isBot
      ? "/img/botIcon.svg"
      : msg.userPic ||
        "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

    const bubbleClass = isBot ? "bot-bubble" : "user-bubble";
    const containerAlign = isBot ? "flex-row" : "flex-row-reverse";
    const iconAlign = isBot ? "justify-content-start" : "justify-content-end";
    const iconMargin = isBot ? "ms-3" : "me-3";

    return (
      <div
        className={`d-flex ${containerAlign} align-items-start my-3  `}
        key={msg.id || index} // ✅ Unique key fallback
      >
        <div className="d-flex flex-column " style={{ maxWidth: "75%" }}>
          <div className={`chat-bubble ${bubbleClass}`}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <img
                src={profile}
                className={isBot ? "bot-avatar" : "chat-avatar"}
                alt="avatar"
              />
              <strong>
                {isBot ? branding("ZeroBox") : msg.userName || "User"}
              </strong>
              <small className="text-muted">{msg.time}</small>
            </div>

            <div className="formatted-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={oneDark}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{
                          borderRadius: "10px",
                          padding: "1rem",
                        }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    ) : (
                      <code
                        className="bg-light px-2 py-1 rounded"
                        style={{ fontFamily: "monospace" }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {msg.message}
              </ReactMarkdown>
            </div>
          </div>

          <div
            className={`d-flex text-secondary align-items-center gap-3 mt-1 ${iconAlign} ${iconMargin}`}
          >
            {isBot && (
              <>
                <img
                  src={speakerIcon}
                  alt="Play Sound"
                  className="icon-image"
                  role="button"
                  title={
                    window.speechSynthesis.speaking &&
                    currentUtterance?.text === msg.message
                      ? "Stop Sound"
                      : "Play Sound"
                  }
                  onClick={() => handleTextToSpeech(msg.message)}
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                    opacity:
                      window.speechSynthesis.speaking &&
                      currentUtterance?.text === msg.message
                        ? 0.6 // Slight fade to indicate “active”
                        : 1,
                    transition: "opacity 0.3s ease",
                  }}
                />
                <img
                  src={cpyIcon}
                  className="icon-image"
                  alt="Copy"
                  role="button"
                  title="Copy"
                  // ✅ FIXED: Copy actual message text, not ID
                  onClick={() => handleCopy(msg.message)}
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-2">
      <div className="flex-grow-1 h-auto overflow-auto ">
        {messages.map((msg, index) => renderMessage(msg, index))}
        <div ref={chatEndRef} />
      </div>
      {/* Input area is intentionally removed for shared view */}
    </div>
  );
};

export default ChatSharedPage;
